import { ServiceKind } from "@bitory-gmbh/bsm";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { Reference } from "@/services/backend/documents/types/reference-number";
import { useConnectDocumentDataShowQuery } from "@/services/backend/connect/api/service";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import { DocumentData } from "@/services/backend/connect/types/document-data";

/**
 * GENERAL COMMENT ON CONNECT EXPORT DESIGN
 *
 * The logic to build the connect export makes some general assumptions about how
 * much space may be needed and the allows the functions transforming the entities
 * to the text lines to rely on them. This is fairly naive since such assumptions
 * may include assuming that "count" cannot be longer than 10 characters including
 * punctuation: effectively 10.000.000 - without any margins - so really only 99.999?
 *
 * This Trade-Off is completely acceptable here. The Connect Export is a temporary
 * solution and such an edge case is extremely unlikely. If users encounter it, they
 * can manually fix it. This is what they have been doing before the GSM.
 *
 * If user requests arise, pragmatically fix as needed.
 */

export const LINE_LENGTH = 100; // estimate from example documents Connect Invoice
const POSITION_NUMBER_LENGTH = 10; // e.g. "1.10.12"
export const POSITION_COUNT_LENGTH = 10; // number + unit e.g. "13 Stk."
export const POSITION_TITLE_LENGTH = 60;
export const POSITION_PRICE_LENGTH = 20; // e.g. "100.234,56 EUR"

export const FIRST_COLUMN_END = POSITION_NUMBER_LENGTH;
export const SECOND_COLUMN_END = POSITION_NUMBER_LENGTH + POSITION_COUNT_LENGTH;
export const THIRD_COLUMN_END = SECOND_COLUMN_END + POSITION_TITLE_LENGTH;
export const FOURTH_COLUMN_END = THIRD_COLUMN_END + POSITION_PRICE_LENGTH;

export function useServiceKindTitle(kind: ServiceKind): string {
  return serviceKindTitle(kind);
}

function serviceKindTitle(kind: ServiceKind): string {
  switch (kind) {
    case GSMServiceKind.HTZ:
      return "inweltschutz - Hygieneinspektion für RLT-Anlagen und -geräte gemäß VDI Richtlinie 6022";
    case GSMServiceKind.RLT:
      return "inweltschutz - Technische Tiefenhygiene für raumlufttechnische Anlagen nach dem Aeroclean-Verfahren";
    case GSMServiceKind.VBS:
      return "inweltschutz - Vorbeugender Brandschutz für Küchenabluftanlagen nach dem Scorpion®-Verfahren";
    default:
      return "";
  }
}

export function useWorkOrderReference(
  workOrder: Reference & { id: string },
  serviceKind: ServiceKind,
): string {
  const { data: connectData } = useConnectDocumentDataShowQuery({
    serviceKind,
    documentKind: GSMDocumentKind.WorkOrder,
    documentId: workOrder.id,
  });

  return workOrderReference(workOrder.referenceNumber, connectData);
}

export function workOrderReference(
  referenceNumber: string,
  connectData?: DocumentData,
): string {
  const line = `Arbeitsschein-Nr.: ${referenceNumber}`;
  if (connectData && connectData.number.length > 0) {
    return `${line} Connect-Nr.: ${connectData.number}`;
  }
  return line;
}

export function useAttachmentsString() {
  return "Anlage: Arbeitsschein";
}
